import React, { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { getTheme, useThemeMode } from '../theme';
import { qTexts } from './i18n';
import { fetchQApps, loadSettings, Q_SETTINGS_EVENT, saveSettings, setQApp, type QAppId, type QRemoteApp, type QSettings } from './qClient';
import { nativeRealtimeAvailable, rtApi, type RtStatus } from './chatgptVoice';

/**
 * Q:s integrationer: andra Q-appar (Q-6) och ChatGPT-kontot (CV2). Används i Q-panelens inställningar och,
 * UX10, under Inställningar → Integrationer – samma status och koppla/koppla från på båda ställena.
 */

const sectionStyle = (T: ReturnType<typeof getTheme>): CSSProperties => ({ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 0', borderBottom: `1px solid ${T.border}` });
const labelStyle = (T: ReturnType<typeof getTheme>): CSSProperties => ({ fontSize: 13, fontWeight: 750, color: T.textSub, textTransform: 'uppercase', letterSpacing: '0.04em' });
const hintStyle = (T: ReturnType<typeof getTheme>): CSSProperties => ({ fontSize: 12.5, color: T.textMuted, lineHeight: 1.4 });

/** Apparnas läge (hämtas när `active` blir sann och när `refreshKey` ändras) och switchen med optimistisk visning. */
export function useQApps(active: boolean, refreshKey?: unknown, onNotice?: (text: string | null) => void) {
  const t = useMemo(() => qTexts(), []);
  const [apps, setApps] = useState<QRemoteApp[] | null>(null);
  const [appsError, setAppsError] = useState(false);
  useEffect(() => {
    if (!active) return;
    let alive = true;
    void fetchQApps().then(a => { if (!alive) return; setAppsError(!a); if (a) setApps(a); });
    return () => { alive = false; };
  }, [active, refreshKey]);

  const toggleApp = useCallback(async (id: QAppId, on: boolean) => {
    setApps(a => a?.map(x => (x.id === id ? { ...x, status: on ? 'on' : 'off', pending: true } as QRemoteApp : x)) ?? a);
    try {
      const next = await setQApp(id, on);
      setApps(a => a?.map(x => (x.id === id ? next : x)) ?? a);
      onNotice?.(next.status === 'no_account' ? t.appNoAccount(next.name) : next.status === 'unavailable' ? `${next.name}: ${t.appUnavailable}` : null);
    } catch (err) {
      onNotice?.((err as Error)?.message || t.error);
      void fetchQApps().then(a => a && setApps(a));
    }
  }, [t, onNotice]);
  return { apps, appsError, toggleApp };
}

/** Switcharna för andra Q-appar. `hideWhenEmpty`: Q-panelen döljer avsnittet när det inte finns någon app att koppla. */
export function QAppsSection({ apps, appsError, onToggleApp, hideWhenEmpty = false }: {
  apps: QRemoteApp[] | null;
  appsError: boolean;
  onToggleApp: (id: QAppId, on: boolean) => void;
  hideWhenEmpty?: boolean;
}) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const t = useMemo(() => qTexts(), []);
  const section = sectionStyle(T);
  const label = labelStyle(T);
  const hint = hintStyle(T);
  if (hideWhenEmpty && apps && !apps.some(a => a.status !== 'no_account')) return null;
  return (
    <div style={section} data-q-apps>
      <div style={label}>{t.apps}</div>
      <div style={hint}>{t.appsHint}</div>
      {/* Bara appar där personen kan ha konto. Q work visas inte (beslut 2). */}
      {!apps ? <div style={hint}>{appsError ? t.appsError : t.appsChecking}</div> : (() => {
        const shown = apps.filter(a => a.status !== 'no_account');
        if (!shown.length) return <div style={hint}>{t.appsNone}</div>;
        return shown.map(a => {
          const busy = !!(a as { pending?: boolean }).pending;
          const on = a.status === 'on' || a.status === 'unavailable'; // unavailable: påslagen men svarar inte
          return (
            <SwitchRow
              key={a.id}
              label={a.name}
              hint={busy ? t.appsChecking : a.status === 'on' ? t.appOnHint(a.tools ?? 0) : a.status === 'unavailable' ? t.appUnavailable : t.appOffHint}
              on={on}
              disabled={busy}
              onToggle={() => onToggleApp(a.id, !on)}
            />
          );
        });
      })()}
    </div>
  );
}

/**
 * UX10: Q:s integrationer under Integrationer. Inställningarna (ChatGPT-kontot) är samma som Q-panelens –
 * sparas per person i webbläsaren (qClient) och läses om av panelen när den öppnas.
 */
export function QIntegrationsSection({ sub }: { sub: string }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [settings, setSettingsState] = useState<QSettings>(() => loadSettings(sub));
  useEffect(() => {
    setSettingsState(loadSettings(sub));
    // Ändras valet i Q-panelen medan kortet är öppet.
    const h = () => setSettingsState(loadSettings(sub));
    window.addEventListener(Q_SETTINGS_EVENT, h);
    return () => window.removeEventListener(Q_SETTINGS_EVENT, h);
  }, [sub]);
  const [notice, setNotice] = useState<string | null>(null);
  const { apps, appsError, toggleApp } = useQApps(true, undefined, setNotice);
  return (
    <div data-testid="q-integrations">
      <QAppsSection apps={apps} appsError={appsError} onToggleApp={(id, on) => void toggleApp(id, on)} />
      {notice && <div role="status" style={{ ...hintStyle(T), paddingTop: 8 }}>{notice}</div>}
      <ChatGPTSettings settings={settings} onChange={s => { setSettingsState(s); saveSettings(sub, s); }} />
    </div>
  );
}

/** ChatGPT-rösten: Qvenaris konto eller egen OpenAI-nyckel (CV2). Nyckeln går till servern och visas aldrig igen. */
export function ChatGPTSettings({ settings, onChange }: { settings: QSettings; onChange: (s: QSettings) => void }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const t = useMemo(() => qTexts(), []);
  const [status, setStatus] = useState<RtStatus | null>(null);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; error: boolean } | null>(null);
  useEffect(() => { void rtApi.status().then(setStatus).catch(e => setMsg({ text: (e as Error).message, error: true })); }, []);

  const account = settings.openaiAccount ?? 'qvenari';
  const section: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 0', borderBottom: `1px solid ${T.border}` };
  const label: CSSProperties = { fontSize: 13, fontWeight: 750, color: T.textSub, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const hint: CSSProperties = { fontSize: 12.5, color: T.textMuted, lineHeight: 1.4 };
  const btn: CSSProperties = { padding: '8px 14px', borderRadius: 999, border: `1px solid ${T.border}`, background: 'transparent', color: T.text, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 };

  const save = async () => {
    setBusy(true);
    setMsg({ text: t.rtKeyChecking, error: false });
    try {
      setStatus(await rtApi.saveKey(key.trim()));
      setKey('');
      setMsg(null);
      onChange({ ...settings, openaiAccount: 'own' });
    } catch (e) {
      setMsg({ text: (e as Error).message, error: true });
    } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { setStatus(await rtApi.removeKey()); setMsg(null); } catch (e) { setMsg({ text: (e as Error).message, error: true }); } finally { setBusy(false); }
  };

  return (
    <div style={section} data-q-chatgpt-settings>
      <div style={label}>{t.rtAccount}</div>
      <div role="radiogroup" aria-label={t.rtAccount} style={{ display: 'flex', borderRadius: 12, background: T.surface2, padding: 3 }}>
        {([['qvenari', t.rtAccountQvenari], ['own', t.rtAccountOwn]] as const).map(([v, l]) => (
          <button key={v} type="button" role="radio" aria-checked={account === v} onClick={() => onChange({ ...settings, openaiAccount: v })} style={{
            flex: 1, padding: '8px 0', borderRadius: 9, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
            background: account === v ? T.surface : 'transparent', color: account === v ? T.text : T.textSub, boxShadow: account === v ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
          }}>{l}</button>
        ))}
      </div>
      {account === 'qvenari' && status && <div style={hint}>{status.qvenari.set || status.mock ? t.rtQvenariUsage(status.qvenari.used, status.qvenari.limit) : t.rtQvenariMissing}</div>}
      {account === 'own' && (status?.own.set ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ ...hint, flex: 1 }}>{t.rtKeySaved(status.own.last4 ?? '')}</div>
          <button type="button" disabled={busy} onClick={() => void remove()} style={{ ...btn, borderColor: T.danger, color: T.danger }}>{t.rtKeyRemove}</button>
        </div>
      ) : (
        <form onSubmit={e => { e.preventDefault(); if (key.trim()) void save(); }} style={{ display: 'flex', gap: 8 }}>
          <input type="password" autoComplete="off" spellCheck={false} aria-label={t.rtAccountOwn} placeholder={t.rtKeyPlaceholder} value={key} onChange={e => setKey(e.target.value)}
            style={{ flex: 1, minWidth: 0, padding: '9px 10px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface2, color: T.text, fontSize: 14, fontFamily: 'inherit' }} />
          <button type="submit" disabled={busy || !key.trim()} style={btn}>{t.rtKeySave}</button>
        </form>
      ))}
      {account === 'own' && !status?.own.set && <div style={hint}>{t.rtKeyHint}</div>}
      {msg && <div role={msg.error ? 'alert' : 'status'} style={{ ...hint, color: msg.error ? T.danger : T.textMuted }}>{msg.text}</div>}
      {nativeRealtimeAvailable() && (
        <SwitchRow label={t.rtBackground} hint={t.rtBackgroundHint} on={settings.openaiBackground !== false}
          onToggle={() => onChange({ ...settings, openaiBackground: settings.openaiBackground === false })} />
      )}
      <div style={hint}>{t.rtBilling}</div>
    </div>
  );
}

export function SwitchRow({ label, hint, badge, on, onToggle, disabled }: { label: string; hint?: string; badge?: string; on: boolean; onToggle?: () => void; disabled?: boolean }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: disabled ? 0.55 : 1, padding: '4px 0' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 650, color: T.text, display: 'flex', alignItems: 'center', gap: 8 }}>
          {label}{badge && <span style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, background: T.surface2, borderRadius: 999, padding: '2px 8px' }}>{badge}</span>}
        </div>
        {hint && <div style={{ fontSize: 12.5, color: T.textMuted, lineHeight: 1.4, marginTop: 2 }}>{hint}</div>}
      </div>
      <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={onToggle} style={{
        width: 46, height: 28, borderRadius: 14, border: 'none', padding: 3, cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0,
        background: on ? T.amber : T.segEmpty, display: 'flex', justifyContent: on ? 'flex-end' : 'flex-start', transition: 'background .15s',
      }}>
        <span style={{ width: 22, height: 22, borderRadius: 11, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }} />
      </button>
    </div>
  );
}
